"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { usePostHog } from "posthog-js/react";
import { LuX } from "react-icons/lu";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.xephyrlabs.lattuai";
const DISMISS_KEY = "lattu_app_install_banner_dismissed";
const SHOW_DELAY_MS = 5000;

const isAndroidChrome = (ua: string) =>
  /Android/.test(ua) &&
  /Chrome\//.test(ua) &&
  !/SamsungBrowser|OPR|Edg|Firefox|Vivaldi/.test(ua);

const AppInstallBanner = () => {
  const posthog = usePostHog();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (!isAndroidChrome(navigator.userAgent)) return;

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-150 p-3 sm:p-4">
      <div className="relative max-w-md mx-auto flex items-center gap-3 bg-paper dark:bg-paper-3 border border-rule-2 rounded-2xl shadow-2xl px-4 py-3">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-paper-2 dark:bg-paper-3 text-muted flex items-center justify-center hover:bg-rule dark:hover:bg-paper-2 transition-colors"
        >
          <LuX size={14} />
        </button>
        <Image
          src="/images/logo_b.svg"
          alt="Lattu.ai"
          width={40}
          height={40}
          className="rounded-xl shrink-0 dark:invert"
        />
        <p className="flex-1 text-sm font-semibold text-ink dark:text-ink leading-snug">
          Install the Lattu.ai app
        </p>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            posthog?.capture("app_download_click", {
              app_platform: "android",
              placement: "install_banner",
              source_page: "homepage",
              cta_text: "google_play_badge",
            });
            dismiss();
          }}
          className="shrink-0 flex items-center gap-2 px-3 py-2 bg-accent dark:bg-accent border border-accent-hover opacity-70 w-fit rounded-xl hover:scale-105 transition-transform"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-accent-ink shrink-0">
            <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-7.28-2.7-2.7zM.29 1.03C.1 1.35 0 1.73 0 2.15v19.7c0 .42.1.8.29 1.12l.06.06 11.04-11.04v-.26L.35.97zM20.38 10.4l-2.92-1.69-3.03 3.03 3.03 3.03 2.94-1.7c.84-.49.84-1.28-.02-1.67zM4.17.24l12.6 7.28-2.7 2.7z" />
          </svg>

          <div className="text-left">
            <p className="text-[10px] leading-none text-accent-ink/60">Get it on</p>
            <p className="text-sm font-semibold leading-tight text-accent-ink">
              Google Play
            </p>
          </div>
        </a>
      </div>
    </div>
  );
};

export default AppInstallBanner;
